import { privateApiClient, publicApiClient } from './apiClient';

const normalizePos = (raw) => ({
    id: raw.id,
    name: raw.name || `TERMINAL ${raw.tag || ''}`,
    description: raw.description || '',
    tag: raw.tag || raw.hTag || raw.h_tag || '',
    organizationId: raw.organizationId || raw.company_id,
    managerId: raw.manager_id || null,
    manager: raw.manager || null,
    site_id: raw.site_id || raw.site?.id || null,
    sale_point_id: raw.sale_point_id || raw.salePointId || raw.site_id || raw.site?.id || null,
    salePoint: raw.salePoint || raw.site || null,
    enabled: raw.enabled !== undefined ? raw.enabled : true,
    isDefault: Boolean(raw.is_default ?? raw.useByDefault),
    favoriteCount: Array.isArray(raw.onFavoris) ? raw.onFavoris.length : 0,
    createdAt: raw.createdAt || null
});

/**
 * Payload terminal (PaymentTerminalRqsDTO). `is_default` doit toujours être envoyé :
 * le pos-service master-dev le lit sans contrôle de null (erreur 500 sinon).
 */
function buildTerminalWritePayload({ companyId, managerId, siteId = null, isDefault = false }) {
    const payload = {
        company_id: companyId,
        manager_id: managerId || '',
        is_default: Boolean(isDefault),
    };
    if (siteId) {
        payload.site_id = siteId;
        payload.sale_point_id = siteId;
    }
    return payload;
}

/** Libellé lisible pour listes / sélecteurs (tag, nom). */
export const formatTerminalOptionLabel = (pos) => {
    if (!pos) return 'Terminal';
    const tag = pos.tag || pos.h_tag || '';
    const baseName = pos.name || (tag ? `TERMINAL ${tag}` : 'Terminal');
    if (tag && baseName && !String(baseName).includes(tag)) {
        return `${baseName} (${tag})`;
    }
    return baseName || tag || 'Terminal';
};

export const fetchCompanyPos = async (organizationId) => {
    // Dans le mobile, on utilise /pos/payment-terminals avec le paramètre org
    try {
        const params = {
            pge: 1,
            lmt: 100,
            slg: ''
        };
        if (organizationId) {
            params.org = organizationId;
        }
        
        const { data } = await privateApiClient.get('/pos/payment-terminals', {
            params
        });
        const raw =
            data?.contents ??
            (Array.isArray(data?.payload) ? data.payload : data?.payload?.contents) ??
            [];
        const contents = Array.isArray(raw) ? raw : [];
        return contents.map(normalizePos);
    } catch (error) {
        // Si 404, peut-être que l'endpoint n'existe pas en dev, retourner un tableau vide
        if (error?.response?.status === 404) {
            console.warn('[fetchCompanyPos] Endpoint /pos/payment-terminals non trouvé, retour d\'un tableau vide');
            return [];
        }
        throw error;
    }
};

/** Terminal par défaut de l'organisation (utilisé quand le caissier n'a pas de terminal). */
export const fetchDefaultPos = async (organizationId) => {
    if (!organizationId) return null;
    try {
        const { data } = await privateApiClient.get(`/pos/payment-terminals/${organizationId}/default-one`);
        return data?.id ? normalizePos(data) : null;
    } catch (error) {
        return null;
    }
};

/**
 * Définit un terminal comme terminal par défaut de l'organisation (l'ancien perd ce rôle côté serveur).
 * L'API exige un caissier sur le terminal.
 */
export const setDefaultPos = async (pos, companyId) => {
    const managerId = pos?.managerId || pos?.manager_id;
    if (!managerId) {
        throw new Error('Assignez un caissier à ce terminal avant de le définir par défaut.');
    }
    const payload = buildTerminalWritePayload({
        companyId: pos.organizationId || companyId,
        managerId,
        siteId: pos.site_id || pos.sale_point_id || null,
        isDefault: true,
    });
    try {
        const { data } = await privateApiClient.put(`/pos/payment-terminals/${pos.id}`, payload);
        if (data?.error) throw new Error(data.message || 'Impossible de définir le terminal par défaut');
        return normalizePos(data?.payload || data);
    } catch (error) {
        throw new Error(error?.response?.data?.message || error.message || 'Impossible de définir le terminal par défaut');
    }
};

/**
 * Récupère le terminal assigné au caissier (manager_id = userId).
 * Utilisé côté caissier : un seul terminal, non modifiable.
 * @param {string} organizationId - ID de l'organisation
 * @param {string} userId - ID du caissier (user.id)
 * @returns {Promise<Object|null>} Le terminal du caissier ou null
 */
export const fetchCaissierTerminal = async (organizationId, userId) => {
    try {
        const list = await fetchCompanyPos(organizationId);
        if (!list || list.length === 0) return null;
        const mine = list.filter((p) => (p.managerId || p.manager_id) === userId);
        if (mine.length >= 1) return mine[0];
        if (list.length === 1) return list[0];
        return fetchDefaultPos(organizationId);
    } catch (error) {
        console.error('[fetchCaissierTerminal]', error);
        return null;
    }
};

export const createPos = async ({ companyId, managerId = null, salePointId = null, siteId = null }) => {
    // POS prod : company_id + manager_id obligatoires ; site_id optionnel.
    const resolvedSiteId = siteId ?? salePointId ?? null;
    const payload = buildTerminalWritePayload({
        companyId,
        managerId,
        siteId: resolvedSiteId || null,
    });
    
    console.log('[createPos] Envoi de la requête avec payload:', payload);
    console.log('[createPos] BaseURL du client:', privateApiClient.defaults?.baseURL);
    console.log('[createPos] URL complète qui sera utilisée:', `${privateApiClient.defaults?.baseURL || ''}/pos/payment-terminals`);
    
    const { data } = await privateApiClient.post('/pos/payment-terminals', payload);
    
    console.log('[createPos] Réponse complète de l\'API:', data);
    
    // Vérifier si l'API a retourné une erreur (comme dans le code mobile)
    if (data?.error) {
        const errorMessage = data.message || 'Erreur lors de la création du POS';
        console.error('[createPos] Erreur retournée par l\'API:', errorMessage);
        throw new Error(errorMessage);
    }
    
    // Dans le mobile, la réponse contient { error, message, payload }
    // Le payload contient le terminal créé
    const terminal = data?.payload || data?.contents || data;
    
    if (!terminal || !terminal.id) {
        console.warn('[createPos] Aucun terminal dans la réponse:', data);
        throw new Error('La réponse de l\'API ne contient pas les données attendues');
    }
    
    const normalized = normalizePos(terminal);
    console.log('[createPos] POS créé avec succès:', normalized);
    
    return normalized;
};

/**
 * Mise à jour d'un terminal (pos-service: PUT /pos/payment-terminals/{id}).
 * Payload attendu: PaymentTerminalRqsDTO (company_id, manager_id, sale_point_id?).
 * Le backend ne gère pas le champ "name" ; si name est fourni, on récupère d'abord
 * le terminal pour envoyer un PUT conforme avec company_id et manager_id.
 */
export const updatePosName = async (posId, _name) => {
    try {
        const { data: terminal } = await privateApiClient.get(`/pos/payment-terminals/${posId}`);
        const existingSiteId =
            terminal?.site_id ||
            terminal?.site?.id ||
            terminal?.sale_point_id ||
            terminal?.salePoint?.id ||
            terminal?.salePointId ||
            null;
        const payload = buildTerminalWritePayload({
            companyId: terminal?.company_id || terminal?.companyId,
            managerId: terminal?.manager_id || terminal?.managerId || '',
            siteId: existingSiteId,
            isDefault: terminal?.is_default,
        });
        if (!payload.company_id || !payload.manager_id) {
            throw new Error('Données du terminal incomplètes pour la mise à jour');
        }
        await privateApiClient.put(`/pos/payment-terminals/${posId}`, payload);
        return true;
    } catch (error) {
        if (error?.response?.status === 404) {
            throw new Error('Terminal non trouvé');
        }
        throw error;
    }
};

export const checkPosTag = async (tag) => {
    const normalized = encodeURIComponent(tag);
    // Dans le mobile, cet endpoint est dans /exposed/v1, donc essayons publicApiClient d'abord
    try {
        const { data } = await publicApiClient.get(`/pos/netikash-tag/check/${normalized}`);
        return Boolean(data?.contents);
    } catch (error) {
        // Si 401, peut-être que l'endpoint nécessite une authentification, essayons privateApiClient
        if (error?.response?.status === 401) {
            try {
                const { data } = await privateApiClient.get(`/pos/netikash-tag/check/${normalized}`);
                return Boolean(data?.contents);
            } catch (retryError) {
                if (retryError?.response?.status === 404) {
                    console.warn('[checkPosTag] Endpoint non trouvé, retour de false');
                    return false;
                }
                throw retryError;
            }
        }
        // Si 404, peut-être que l'endpoint n'existe pas, retourner false
        if (error?.response?.status === 404) {
            console.warn('[checkPosTag] Endpoint non trouvé, retour de false');
            return false;
        }
        throw error;
    }
};

export const fetchPosCashier = async (posId, managerId) => {
    // Dans le mobile, le manager_id est directement dans les données du terminal
    // Si on a manager_id, récupérer les infos de l'utilisateur
    if (!managerId) {
        return null;
    }
    
    try {
        const { data } = await privateApiClient.get('/usr/users', {
            params: { ids: managerId }
        });
        const users = data?.contents || [];
        const user = users.find(u => u.id === managerId);
        if (user) {
            const phone = user.phone?.fullNumber || user.phoneNumber || '';
            return phone || user.id;
        }
        return null;
    } catch (error) {
        console.warn('[fetchPosCashier] Erreur lors de la récupération du caissier:', error);
        return null;
    }
};

/**
 * Nom et téléphone des caissiers, en un seul appel /usr/users.
 * @param {string[]} managerIds
 * @returns {Promise<Object>} Map id → { name, phone }
 */
export const fetchCashierProfiles = async (managerIds = []) => {
    const ids = [...new Set(managerIds.filter(Boolean))];
    if (ids.length === 0) return {};
    try {
        const { data } = await privateApiClient.get('/usr/users', {
            params: { ids: ids.join(',') }
        });
        const profiles = {};
        (data?.contents || []).forEach((u) => {
            const phone = u.phone?.fullNumber || u.phoneNumber || '';
            profiles[u.id] = {
                name: u.profile?.completeName || u.name || u.fullName || phone || u.id,
                phone,
            };
        });
        return profiles;
    } catch (error) {
        console.warn('[fetchCashierProfiles]', error);
        return {};
    }
};

export const assignPosCashier = async (posId, managerId, companyId) => {
    // Dans le mobile, on utilise PUT /pos/payment-terminals/{id} avec company_id et manager_id dans le payload
    // Le mobile envoie tout le model, donc on doit inclure company_id et manager_id au minimum
    if (!companyId) {
        throw new Error('ID de l\'organisation requis pour assigner un caissier');
    }
    
    // Récupérer d'abord le terminal pour préserver les autres champs (site_id, etc.)
    let terminalData = {};
    try {
        const { data: terminalsData } = await privateApiClient.get('/pos/payment-terminals', {
            params: { org: companyId, pge: 1, lmt: 100 }
        });
        const terminal = terminalsData?.contents?.find(t => t.id === posId);
        const existingSiteId = terminal
            ? terminal.site_id ||
              terminal.site?.id ||
              terminal.sale_point_id ||
              terminal.salePoint?.id ||
              null
            : null;
        terminalData = buildTerminalWritePayload({
            companyId: terminal?.company_id || companyId,
            managerId,
            siteId: existingSiteId,
            isDefault: terminal?.is_default,
        });
    } catch (error) {
        console.warn('[assignPosCashier] Impossible de récupérer le terminal, utilisation des champs minimaux');
        terminalData = buildTerminalWritePayload({ companyId, managerId, siteId: null });
    }
    
    console.log('[assignPosCashier] Mise à jour du terminal avec payload:', terminalData);
    
    const { data } = await privateApiClient.put(`/pos/payment-terminals/${posId}`, terminalData);
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de l\'assignation du caissier');
    }
    
    return data?.payload || data;
};

export const verifyNetikashPhone = async (phoneNumber) => {
    const { data } = await publicApiClient.get(`/usr/register/check-phone/${phoneNumber}`);
    return data;
};

